import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
} from 'recharts';
import { predictRisk, simulateCrisis } from 'services/api';
import { MOCK_ANALYTICS, MOCK_INCIDENTS, MOCK_RESPONDERS, SEVERITY_LEVELS, INCIDENT_CATEGORIES, DISPATCH_STATUS_CONFIG } from 'utils/mockData';
import { getStatusChipClass, formatTimeAgo } from 'utils/helpers';
import IncidentMap from 'components/common/IncidentMap';
import ConfidenceMeter from 'components/common/ConfidenceMeter';
import IncidentTimeline from 'components/common/IncidentTimeline';
import DispatchChain from 'components/common/DispatchChain';
import SimulationModal from 'components/admin/SimulationModal';
import { VerificationWidget, PostCrisisPanel } from 'components/admin/AdminPanels';
import { initDispatch } from 'services/dispatchStore';
import toast from 'react-hot-toast';
import { LineChart as LC, Line as L } from 'recharts';

const PIE_COLORS = { fire: '#FF3B3B', medical: '#1E90FF', flood: '#00C896', police: '#7C3AED', other: '#FFB300' };

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return <div className="font-display font-bold text-electric text-xl tracking-wider">{time.toLocaleTimeString('en-IN', { hour12: false })}</div>;
}

function AnimatedCount({ value }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const s = Date.now();
    const step = () => { const p = Math.min((Date.now() - s) / 800, 1); setN(Math.round(p * value)); if (p < 1) requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }, [value]);
  return <span>{n.toLocaleString()}</span>;
}

function StatCard({ label, value, icon, color, spark }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-4" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-gray-400 text-xs font-mono uppercase tracking-wider">{label}</span>
        <span>{icon}</span>
      </div>
      <div className="font-display font-black text-2xl mb-1" style={{ color }}>
        {typeof value === 'number' ? <AnimatedCount value={value} /> : value}
      </div>
      {spark && (
        <div className="h-8 mt-1">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={spark.map((v, i) => ({ i, v }))}>
              <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}

// Responder Status Board per category
function ResponderBoard() {
  const byType = ['firefighter', 'paramedic', 'police', 'rescue'];
  const typeConfig = {
    firefighter: { label: 'Firefighters', icon: '🔥', color: '#FF3B3B' },
    paramedic:   { label: 'Paramedics',   icon: '🚑', color: '#1E90FF' },
    police:      { label: 'Police',        icon: '🚔', color: '#7C3AED' },
    rescue:      { label: 'Rescue',        icon: '🌊', color: '#00C896' },
  };
  return (
    <div className="glass-card p-5">
      <h2 className="font-display font-bold text-sm text-white mb-4">👮 Responder Status Board</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {byType.map((type) => {
          const cfg = typeConfig[type];
          const responders = MOCK_RESPONDERS.filter((r) => r.type === type);
          return (
            <div key={type} className="rounded-xl border overflow-hidden" style={{ borderColor: cfg.color + '30' }}>
              <div className="px-4 py-2 flex items-center gap-2" style={{ background: cfg.color + '15' }}>
                <span>{cfg.icon}</span>
                <span className="font-bold text-sm" style={{ color: cfg.color }}>{cfg.label}</span>
                <span className="ml-auto text-xs font-mono text-gray-400">
                  {responders.filter((r) => r.status === 'available').length}/{responders.length} available
                </span>
              </div>
              <div className="divide-y divide-navy-border">
                {responders.map((r) => {
                  const statusColors = { available: '#00C896', 'en-route': '#FFB300', 'on-scene': '#FF3B3B', 'off-duty': '#666' };
                  const sc = statusColors[r.status] || '#888';
                  return (
                    <div key={r.id} className="px-4 py-2.5 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: cfg.color + '20', color: cfg.color }}>
                        {r.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{r.name}</div>
                        <div className="text-xs text-gray-400">{r.badge} · {r.distance}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full" style={{ background: sc }} />
                        <span className="text-xs font-mono" style={{ color: sc }}>{r.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Full Dispatch Board for all active incidents
function DispatchBoard() {
  const [expanded, setExpanded] = useState(null);
  const active = MOCK_INCIDENTS.filter((i) => i.status !== 'resolved');

  return (
    <div className="glass-card overflow-hidden">
      <div className="p-5 border-b border-navy-border">
        <h2 className="font-display font-bold text-sm text-white">📡 Live Dispatch Board</h2>
        <p className="text-xs text-gray-400 mt-0.5">Real-time responder assignments · Chain of responsibility</p>
      </div>
      <div className="divide-y divide-navy-border">
        {active.map((inc) => {
          const cat = INCIDENT_CATEGORIES[inc.type] || {};
          const sev = SEVERITY_LEVELS[inc.severity] || {};
          const primary = inc.dispatchChain?.[0];
          const primaryResp = primary ? MOCK_RESPONDERS.find((r) => r.id === primary.responderId) : null;
          const primaryCfg = primary ? DISPATCH_STATUS_CONFIG[primary.status] : null;

          return (
            <div key={inc.id}>
              <div
                className="px-5 py-4 cursor-pointer hover:bg-navy-card/50 transition-colors"
                onClick={() => setExpanded(expanded === inc.id ? null : inc.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{cat.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="font-bold text-sm text-white">{inc.subcategory}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: sev.bg, color: sev.color }}>{sev.label}</span>
                      <span className="font-mono text-xs text-gray-400">{inc.id}</span>
                    </div>
                    <div className="text-xs text-gray-400">{inc.location.address} · {formatTimeAgo(inc.createdAt)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {primaryCfg && primaryResp && (
                      <div className="text-right">
                        <div className="text-xs" style={{ color: primaryCfg.color }}>{primaryCfg.icon} {primaryCfg.label}</div>
                        <div className="text-xs text-gray-400">{primaryResp.name}</div>
                      </div>
                    )}
                    <ConfidenceMeter score={inc.verificationScore} size={38} label="" />
                    <span className="text-gray-400 text-sm">{expanded === inc.id ? '▲' : '▼'}</span>
                  </div>
                </div>
              </div>
              <AnimatePresence>
                {expanded === inc.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5">
                      <DispatchChain incident={inc} currentResponderId={null} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [analytics] = useState(MOCK_ANALYTICS);
  const [mapFilter, setMapFilter] = useState('all');
  const [showSim, setShowSim] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simScenario, setSimScenario] = useState('Mass Casualty');
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [predLoading, setPredLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview | dispatch | responders | analytics

  useEffect(() => {
    MOCK_INCIDENTS.forEach((inc) => initDispatch(inc.id, inc.dispatchChain || []));
  }, []);

  const filteredIncidents = MOCK_INCIDENTS.filter((i) => mapFilter === 'all' || i.type === mapFilter);

  const runPrediction = async () => {
    setPredLoading(true);
    try {
      const res = await predictRisk({ zoneId: 'zone-1', currentIncidents: MOCK_INCIDENTS, timeOfDay: new Date().getHours() });
      setPrediction(res.data);
    } catch {
      setPrediction({ riskScore: 72, riskLevel: 'high', predictedIncidentTypes: ['Road accident', 'Medical emergency', 'Riot / civil unrest'], timeToIncident: '30–60 minutes', preventiveActions: ['Deploy police patrol near Old Town Market', 'Keep medical units on NH-16 standby', 'Increase CCTV monitoring in Nayapalli area'], confidenceLevel: 85 });
    } finally {
      setPredLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimLoading(true);
    setSimulation(null);
    try {
      const res = await simulateCrisis({ scenario: simScenario, location: 'Bhubaneswar', peopleCount: 500 });
      setSimulation(res.data);
    } catch {
      setSimulation({ totalDuration: '4h 30m', successProbability: 78, casualtyEstimate: '12–18 critical, 45 minor', evacuationRoute: 'Ring Road → NH-16. Alternate: NH-57', phases: [{ phase: 'Containment', duration: '0–30 min', actions: ['Secure perimeter', 'Deploy units', 'Command post'], resources: 'Unit Alpha + Bravo' }, { phase: 'Rescue', duration: '30–90 min', actions: ['Search & rescue', 'Triage zone B', 'Air evac'], resources: '3 medical teams' }, { phase: 'Stabilization', duration: '90–180 min', actions: ['Hazmat control', 'Utilities', 'Secondary sweep'], resources: 'HAZMAT + Police' }, { phase: 'Recovery', duration: '180–270 min', actions: ['Debrief', 'Documentation', 'Assessment'], resources: 'Admin + Engineering' }], criticalDecisionPoints: ['T+15min: Evacuation order?', 'T+60min: Request mutual aid?', 'T+120min: State of emergency?'] });
    } finally {
      setSimLoading(false);
    }
  };

  const pieData = Object.entries(analytics.incidentsByType).map(([name, value]) => ({ name, value }));
  const tabs = [
    { key: 'overview', label: '📊 Overview' },
    { key: 'dispatch', label: '📡 Dispatch Board' },
    { key: 'responders', label: '👮 Responders' },
    { key: 'analytics', label: '📈 Analytics' },
  ];

  return (
    <div className="min-h-screen bg-navy pb-10">
      {/* Command header */}
      <div className="bg-navy-card border-b border-navy-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-5">
            <LiveClock />
            <div className="h-6 w-px bg-navy-border" />
            {[{ l: 'Active', v: analytics.activeCount, c: '#FF3B3B' }, { l: 'Pending', v: analytics.pendingCount, c: '#FFB300' }, { l: 'Responders', v: MOCK_RESPONDERS.filter((r) => r.status === 'available').length, c: '#00C896' }].map((s) => (
              <div key={s.l} className="text-center">
                <div className="font-display font-bold text-lg" style={{ color: s.c }}><AnimatedCount value={s.v} /></div>
                <div className="text-xs text-gray-400">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald animate-pulse" />
            <span className="text-xs font-mono text-emerald">All Systems Operational</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-navy-border bg-navy-card">
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto py-1">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === t.key ? 'bg-electric text-navy' : 'text-gray-400 hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5 space-y-5">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Today" value={analytics.totalToday} icon="📊" color="#1E90FF" spark={analytics.sparklines.incidents} />
              <StatCard label="Avg Response" value={analytics.avgResponseTime} icon="⚡" color="#00C896" spark={analytics.sparklines.responseTime} />
              <StatCard label="False Alarm %" value={analytics.falseAlarmRate} icon="🎯" color="#FFB300" spark={analytics.sparklines.falseAlarm} />
              <StatCard label="Lives Protected" value={analytics.livesProtected} icon="🛡️" color="#FF3B3B" spark={analytics.sparklines.lives} />
            </div>

            {/* Map + Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <h2 className="font-display font-bold text-sm text-white mr-2">🗺️ Live Incident Map</h2>
                  {['all', ...Object.keys(INCIDENT_CATEGORIES)].map((f) => {
                    const cfg = INCIDENT_CATEGORIES[f];
                    return (
                      <button key={f} onClick={() => setMapFilter(f)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${mapFilter === f ? 'bg-electric text-navy' : 'bg-navy-card border border-navy-border text-gray-400 hover:text-white'}`}>
                        {cfg?.icon} {f === 'all' ? 'All' : cfg?.label}
                      </button>
                    );
                  })}
                </div>
                <IncidentMap incidents={filteredIncidents} height="340px" />
              </div>
              <div className="space-y-4">
                <div className="glass-card p-4">
                  <h3 className="font-display font-bold text-xs text-white mb-3">Incidents by Category</h3>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={68} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                        {pieData.map((e) => <Cell key={e.name} fill={PIE_COLORS[e.name] || '#888'} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#141B2D', border: '1px solid #1E2A45', borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Severity breakdown */}
                <div className="glass-card p-4">
                  <h3 className="font-display font-bold text-xs text-white mb-3">Severity Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(SEVERITY_LEVELS).map(([key, sev]) => {
                      const count = MOCK_INCIDENTS.filter((i) => i.severity === key).length;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-xs font-mono w-16" style={{ color: sev.color }}>{sev.label}</span>
                          <div className="flex-1 bg-navy-border rounded-full h-1.5 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(count / MOCK_INCIDENTS.length) * 100}%`, background: sev.color }} />
                          </div>
                          <span className="text-xs text-gray-400 w-4">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Prediction */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="glass-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display font-bold text-sm text-white">🔮 AI Risk Prediction</h2>
                  <button onClick={runPrediction} disabled={predLoading} className="px-4 py-1.5 rounded-xl text-xs font-bold bg-electric/10 border border-electric/40 text-electric hover:bg-electric/20 disabled:opacity-50">
                    {predLoading ? 'Analyzing...' : 'Run Prediction'}
                  </button>
                </div>
                {prediction ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                    <div className="flex items-center gap-4">
                      <ConfidenceMeter score={prediction.riskScore} size={64} label="Risk" />
                      <div>
                        <div className="text-lg font-bold" style={{ color: prediction.riskLevel === 'high' ? '#FF3B3B' : prediction.riskLevel === 'medium' ? '#FFB300' : '#00C896' }}>{prediction.riskLevel?.toUpperCase()} RISK</div>
                        <div className="text-xs text-gray-400">Confidence: {prediction.confidenceLevel}%</div>
                        <div className="text-xs text-gray-400">Next: {prediction.timeToIncident}</div>
                      </div>
                    </div>
                    <div><div className="text-xs font-mono text-amber mb-1">PREDICTED INCIDENTS</div>{prediction.predictedIncidentTypes?.map((t, i) => <div key={i} className="text-sm text-gray-300">• {t}</div>)}</div>
                    <div><div className="text-xs font-mono text-emerald mb-1">PREVENTIVE ACTIONS</div>{prediction.preventiveActions?.map((a, i) => <div key={i} className="text-sm text-gray-300">→ {a}</div>)}</div>
                  </motion.div>
                ) : (
                  <div className="text-center py-6 text-gray-400 text-sm"><div className="text-4xl mb-2">🔮</div>Run AI prediction to forecast zone risk for next 2 hours</div>
                )}
              </div>
              <div className="space-y-4">
                <h2 className="font-display font-bold text-sm text-white">✅ Verification Queue</h2>
                {MOCK_INCIDENTS.filter((i) => i.status === 'pending').slice(0, 2).map((inc) => (
                  <VerificationWidget key={inc.id} incident={inc} />
                ))}
              </div>
            </div>

            {/* Incident table with timeline */}
            <div className="glass-card overflow-hidden">
              <div className="p-5 border-b border-navy-border">
                <h2 className="font-display font-bold text-sm text-white">📋 Incident Management</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-navy-border">
                      {['ID', 'Category', 'Subcategory', 'Location', 'Severity', 'Status', 'Dispatch', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-mono text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_INCIDENTS.map((inc, i) => {
                      const cat = INCIDENT_CATEGORIES[inc.type] || {};
                      const sev = SEVERITY_LEVELS[inc.severity] || {};
                      const primary = inc.dispatchChain?.[0];
                      const primaryCfg = primary ? DISPATCH_STATUS_CONFIG[primary.status] : null;
                      const primaryResp = primary ? MOCK_RESPONDERS.find((r) => r.id === primary.responderId) : null;
                      return (
                        <motion.tr key={inc.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }} className="border-b border-navy-border hover:bg-navy-card transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-electric whitespace-nowrap">{inc.id}</td>
                          <td className="px-4 py-3"><span className="flex items-center gap-1"><span>{cat.icon}</span><span className="text-xs capitalize" style={{ color: cat.color }}>{inc.type}</span></span></td>
                          <td className="px-4 py-3 text-xs text-white whitespace-nowrap">{inc.subcategory}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs max-w-[130px] truncate">{inc.location.address}</td>
                          <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: sev.bg, color: sev.color }}>{sev.label}</span></td>
                          <td className="px-4 py-3"><span className={`chip ${getStatusChipClass(inc.status)}`}>{inc.status}</span></td>
                          <td className="px-4 py-3">
                            {primaryCfg && primaryResp ? (
                              <div className="flex items-center gap-1 text-xs" style={{ color: primaryCfg.color }}>
                                <span>{primaryCfg.icon}</span>
                                <span>{primaryResp.name}</span>
                              </div>
                            ) : <span className="text-xs text-gray-500">Unassigned</span>}
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => setSelectedTimeline(selectedTimeline?.id === inc.id ? null : inc)} className="text-xs px-2 py-1 rounded bg-electric/10 border border-electric/30 text-electric hover:bg-electric/20">
                              {selectedTimeline?.id === inc.id ? 'Close' : 'Timeline'}
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <AnimatePresence>
              {selectedTimeline && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="glass-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display font-bold text-sm text-white">📅 Timeline: {selectedTimeline.id}</h2>
                    <button onClick={() => setSelectedTimeline(null)} className="text-gray-400 hover:text-white">✕</button>
                  </div>
                  <IncidentTimeline incident={selectedTimeline} />
                </motion.div>
              )}
            </AnimatePresence>

            <PostCrisisPanel />

            {/* Digital twin */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-sm text-white">🎮 Digital Twin Simulation</h2>
                <button onClick={() => { setSimulation(null); setShowSim(true); }} className="px-4 py-2 rounded-xl font-bold text-sm bg-crimson text-white hover:bg-crimson-dark">Launch</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Mass Casualty', 'Building Fire', 'Flash Flood', 'Riot Control'].map((s) => (
                  <button key={s} onClick={() => { setSimScenario(s); setSimulation(null); setShowSim(true); }}
                    className="p-3 rounded-xl border border-navy-border bg-navy text-sm text-gray-300 hover:border-crimson hover:text-crimson text-center transition-all">
                    {s === 'Mass Casualty' ? '💀' : s === 'Building Fire' ? '🔥' : s === 'Flash Flood' ? '🌊' : '🚔'} {s}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* DISPATCH BOARD TAB */}
        {activeTab === 'dispatch' && <DispatchBoard />}

        {/* RESPONDERS TAB */}
        {activeTab === 'responders' && <ResponderBoard />}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div className="space-y-5">
            <div className="glass-card p-5">
              <h2 className="font-display font-bold text-sm text-white mb-4">📈 Incidents by Hour (Last 24h)</h2>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={analytics.incidentsByHour}>
                  <defs>
                    <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1E90FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1E90FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="hour" stroke="#444" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis stroke="#444" tick={{ fill: '#888', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#141B2D', border: '1px solid #1E2A45', borderRadius: 8, color: '#fff' }} />
                  <Area type="monotone" dataKey="count" stroke="#1E90FF" fill="url(#ag)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card p-5">
              <h2 className="font-display font-bold text-sm text-white mb-4">🏷️ Incidents by Category</h2>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Object.entries(analytics.incidentsByType).map(([name, value]) => ({ name, value }))}>
                  <XAxis dataKey="name" stroke="#444" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis stroke="#444" tick={{ fill: '#888', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#141B2D', border: '1px solid #1E2A45', borderRadius: 8, color: '#fff' }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {Object.entries(analytics.incidentsByType).map(([name]) => (
                      <Cell key={name} fill={PIE_COLORS[name] || '#888'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showSim && (
          <SimulationModal scenario={simScenario} setScenario={setSimScenario}
            onClose={() => { setShowSim(false); setSimulation(null); }}
            onRun={runSimulation} loading={simLoading} result={simulation} />
        )}
      </AnimatePresence>
    </div>
  );
}
